import { Link } from 'react-router-dom';
import type { NewsItem } from './types';

interface FeaturedNewsSectionProps {
  newsData: NewsItem[];
  loading: boolean;
  isVisible: boolean;
  sectionRef: React.RefObject<HTMLElement | null>;
  name?: string;
  description?: string;
  background?: string;
  nameColor?: string;
  descriptionColor?: string;
}

export default function FeaturedNewsSection({
  newsData,
  loading,
  isVisible,
  sectionRef,
  name = 'Tin Tức Mới Nhất',
  description = 'Cập nhật những xu hướng công nghệ mới nhất',
  background,
  nameColor,
  descriptionColor
}: FeaturedNewsSectionProps) {
  const backgroundStyle = background
    ? {
        backgroundImage: background.startsWith('http') || background.startsWith('/')
          ? `url(${background})`
          : undefined,
        backgroundColor: background.startsWith('http') || background.startsWith('/') ? undefined : background,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }
    : undefined;

  return (
    <section 
      ref={sectionRef}
      data-section="news"
      className={`py-24 ${!background ? 'bg-white' : ''}`}
      style={backgroundStyle}
    >
      <div className="max-w-7xl mx-auto px-6">
        <div className={`text-center mb-16 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          <h2 
            className="text-4xl font-bold mb-4"
            style={{ color: nameColor || '#333' }}
          >
            {name}
          </h2>
          {description && (
            <p 
              className="text-lg max-w-2xl mx-auto"
              style={{ color: descriptionColor || '#4b5563' }}
            >
              {description}
            </p>
          )}
        </div>
        {newsData.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {newsData.map((news, index) => (
              <article
                key={news._id}
                className={`group bg-white rounded-xl overflow-hidden border-2 border-gray-100 hover:border-[#00c897] transition-all duration-700 hover:shadow-xl cursor-pointer ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
                }`}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <div className="relative h-56 overflow-hidden">
                  <img
                    src={news.image}
                    alt={news.title}
                    className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="px-3 py-1 bg-[#00c897] text-white text-xs font-semibold rounded-full">
                      {news.newCategory?.name || news.newCategoryId?.name}
                    </span>
                  </div>
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-4 text-sm text-[#4b5563] mb-3">
                    <span className="flex items-center gap-1">
                      <i className="ri-user-line"></i>
                      {news.author}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-time-line"></i>
                      {news.readTime}
                    </span>
                  </div>
                  <h3 className="text-xl font-bold text-[#333] mb-3 group-hover:text-[#00c897] transition-colors">
                    {news.title}
                  </h3>
                  <p className="text-[#4b5563] leading-relaxed mb-4 line-clamp-3">
                    {news.excerpt}
                  </p>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {news.tags.slice(0, 3).map((tag, idx) => (
                      <span
                        key={idx}
                        className="px-2 py-1 bg-gray-100 text-[#4b5563] text-xs rounded"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                  <Link
                    to={`/news/${news.slug}`}
                    className="inline-flex items-center gap-2 text-[#00c897] font-semibold hover:gap-3 transition-all duration-300"
                  >
                    Đọc thêm
                    <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div className={`text-center py-12 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
            {loading ? (
              <div className="flex flex-col items-center gap-4">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#00c897]"></div>
                <p className="text-[#4b5563]">Đang tải tin tức...</p>
              </div>
            ) : (
              <p className="text-[#4b5563] text-lg">Hiện tại chưa có tin tức nào. Vui lòng quay lại sau!</p>
            )}
          </div>
        )}
      </div>
    </section>
  );
}

